import { Component, Input, OnInit, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ClubModel } from '../../../core/models/app.models';
import { ClubService } from '../../../core/services/club.service';
import { Subject, debounceTime, distinctUntilChanged, switchMap, of } from 'rxjs';

@Component({
  selector: 'app-club-select',
  template: `
    <p-dropdown [options]="clubs" 
                [(ngModel)]="value" 
                [showClear]="showClear" 
                [filter]="true"
                [lazy]="true"
                (onFilter)="onSearch($event)"
                [placeholder]="placeholder" 
                [styleClass]="styleClass"
                [style]="{'width': '100%'}"
                [loading]="loading"
                (onChange)="onChanged($event.value)"
                optionLabel="name" 
                optionValue="id">
      <ng-template let-club pTemplate="item">
        <div class="flex flex-column">
          <span class="font-bold">{{ club.name }}</span>
          <small class="text-500" *ngIf="club.faNumber">FA: {{ club.faNumber }}</small>
        </div>
      </ng-template>
    </p-dropdown>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ClubSelectComponent),
      multi: true
    }
  ]
})
export class ClubSelectComponent implements OnInit, ControlValueAccessor {
  @Input() placeholder: string = 'Vyberte klub';
  @Input() showClear: boolean = true;
  @Input() styleClass: string = '';

  clubs: ClubModel[] = [];
  loading = false;
  private searchSubject = new Subject<string>();

  _value: any;

  constructor(private clubService: ClubService) {}

  ngOnInit() {
    this.loadInitial();

    // Setup debounced search
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => {
        if (!query || query.length < 2) return this.clubService.getAllSimple().pipe(switchMap(res => of(res)));
        this.loading = true;
        return this.clubService.searchClubs(query);
      })
    ).subscribe(data => {
      this.clubs = data;
      this.loading = false;
      // Ensure current selection is in the list
      this.ensureSelectionInList();
    });
  }

  private loadInitial() {
    this.loading = true;
    this.clubService.getAllSimple().subscribe(data => {
      this.clubs = data;
      this.loading = false;
      this.ensureSelectionInList();
    });
  }

  onSearch(event: any) {
    this.searchSubject.next(event.filter);
  }

  private ensureSelectionInList() {
    if (this._value && !this.clubs.find(c => c.id === this._value)) {
      this.clubService.getById(this._value).subscribe(club => {
        if (club && !this.clubs.find(c => c.id === club.id)) {
          this.clubs = [club, ...this.clubs];
        }
      });
    }
  }

  get value() { return this._value; }
  set value(v: any) {
    if (v !== this._value) {
      this._value = v;
      this.onChange(v);
    }
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  writeValue(value: any): void { 
    this._value = value; 
    if (value) this.ensureSelectionInList();
  }
  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }

  onChanged(val: any) {
    this.value = val;
  }
}
